import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import { FaArrowDown, FaArrowUp, FaChevronDown, FaChevronUp, FaExchangeAlt, FaPencilAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { directSupabase } from '../lib/supabaseClient';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { IconWrapper } from '../components/IconWrapper';

type KanbanStatus = 'todo' | 'in_progress' | 'done' | 'rejected_nice_to_have';
type KanbanBoardKey = 'general' | 'simon' | 'james';

type KanbanCardRow = {
  id: string;
  title: string;
  status: KanbanStatus;
  board_key: KanbanBoardKey;
  lane_id: string;
  position: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  deleted_by: string | null;
};

type KanbanLaneRow = {
  id: string;
  title: string;
  position: number;
  is_default: boolean;
  board_key: KanbanBoardKey;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  deleted_by: string | null;
};

type BoardColumn = {
  id: KanbanStatus;
  label: string;
};

const COLUMNS: BoardColumn[] = [
  { id: 'todo', label: 'To Do' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'done', label: 'Done' },
  { id: 'rejected_nice_to_have', label: 'Rejected / Nice to Have' },
];

const CARDS_TABLE = 'jarvis_kanban_cards';
const LANES_TABLE = 'jarvis_kanban_lanes';
const DEFAULT_LANE_PLACEHOLDER = 'Untitled swimlane';
const BOARD_OPTIONS: Array<{ key: KanbanBoardKey; label: string }> = [
  { key: 'general', label: 'General' },
  { key: 'simon', label: 'Simon' },
  { key: 'james', label: 'James' },
];

const sortCards = (cards: KanbanCardRow[]): KanbanCardRow[] => {
  return [...cards].sort((a, b) => {
    if (a.lane_id !== b.lane_id) return a.lane_id.localeCompare(b.lane_id);
    if (a.status !== b.status) return a.status.localeCompare(b.status);
    if (a.position !== b.position) return a.position - b.position;
    return a.created_at.localeCompare(b.created_at);
  });
};

const sortLanes = (lanes: KanbanLaneRow[]): KanbanLaneRow[] => {
  return [...lanes].sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position;
    return a.created_at.localeCompare(b.created_at);
  });
};

const getNextPosition = (cards: KanbanCardRow[], status: KanbanStatus) => {
  const inColumn = cards.filter((card) => card.status === status);
  if (inColumn.length === 0) return 1;
  return Math.max(...inColumn.map((card) => card.position || 0)) + 1;
};

const JarvisKanbanPage: React.FC = () => {
  const { user } = useSupabaseAuth();
  const [loading, setLoading] = useState(true);
  const [selectedBoardKey, setSelectedBoardKey] = useState<KanbanBoardKey>('general');
  const [lanes, setLanes] = useState<KanbanLaneRow[]>([]);
  const [cards, setCards] = useState<KanbanCardRow[]>([]);

  const [addingNoteTarget, setAddingNoteTarget] = useState<{ laneId: string; status: KanbanStatus } | null>(null);
  const [newNoteTitle, setNewNoteTitle] = useState('');

  const [submittingNote, setSubmittingNote] = useState(false);
  const [addingLane, setAddingLane] = useState(false);

  const [draggingCardId, setDraggingCardId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [editingCardTitle, setEditingCardTitle] = useState('');
  const [deletingCardId, setDeletingCardId] = useState<string | null>(null);
  const [cardToDelete, setCardToDelete] = useState<KanbanCardRow | null>(null);
  const [cardToMove, setCardToMove] = useState<KanbanCardRow | null>(null);
  const [moveTargetLaneId, setMoveTargetLaneId] = useState<string>('');
  const [moveTargetStatus, setMoveTargetStatus] = useState<KanbanStatus>('todo');
  const [movingCardId, setMovingCardId] = useState<string | null>(null);

  const [editingLaneId, setEditingLaneId] = useState<string | null>(null);
  const [editingLaneTitle, setEditingLaneTitle] = useState('');
  const [laneToDelete, setLaneToDelete] = useState<KanbanLaneRow | null>(null);
  const [deletingLaneId, setDeletingLaneId] = useState<string | null>(null);
  const [collapsedLaneIds, setCollapsedLaneIds] = useState<Set<string>>(new Set());
  const [movingLaneId, setMovingLaneId] = useState<string | null>(null);

  const [trashOpen, setTrashOpen] = useState(false);
  const [trashLoading, setTrashLoading] = useState(false);
  const [deletedCards, setDeletedCards] = useState<KanbanCardRow[]>([]);
  const [deletedLanes, setDeletedLanes] = useState<KanbanLaneRow[]>([]);
  const [restoringCardId, setRestoringCardId] = useState<string | null>(null);
  const [restoringLaneId, setRestoringLaneId] = useState<string | null>(null);

  const defaultLaneId = lanes.find((lane) => lane.is_default)?.id || lanes[0]?.id || '';
  const laneIdsInOrder = lanes.map((lane) => lane.id);

  const loadTrashItems = useCallback(async () => {
    try {
      setTrashLoading(true);
      const [lanesResult, cardsResult] = await Promise.all([
        (directSupabase as any)
          .from(LANES_TABLE)
          .select('id,title,position,is_default,board_key,created_at,updated_at,deleted_at,deleted_by')
          .eq('board_key', selectedBoardKey)
          .not('deleted_at', 'is', null)
          .order('deleted_at', { ascending: false }),
        (directSupabase as any)
          .from(CARDS_TABLE)
          .select('id,title,status,board_key,lane_id,position,created_at,updated_at,deleted_at,deleted_by')
          .eq('board_key', selectedBoardKey)
          .not('deleted_at', 'is', null)
          .order('deleted_at', { ascending: false }),
      ]);

      if (lanesResult.error) throw lanesResult.error;
      if (cardsResult.error) throw cardsResult.error;

      setDeletedLanes(sortLanes((lanesResult.data || []) as KanbanLaneRow[]));
      setDeletedCards(sortCards((cardsResult.data || []) as KanbanCardRow[]));
    } catch (error) {
      console.error('Failed to load trash items:', error);
    } finally {
      setTrashLoading(false);
    }
  }, [selectedBoardKey]);

  useEffect(() => {
    const loadBoard = async () => {
      try {
        setLoading(true);
        const [lanesResult, cardsResult] = await Promise.all([
          (directSupabase as any)
            .from(LANES_TABLE)
            .select('id,title,position,is_default,board_key,created_at,updated_at,deleted_at,deleted_by')
            .eq('board_key', selectedBoardKey)
            .is('deleted_at', null)
            .order('position', { ascending: true })
            .order('created_at', { ascending: true }),
          (directSupabase as any)
            .from(CARDS_TABLE)
            .select('id,title,status,board_key,lane_id,position,created_at,updated_at,deleted_at,deleted_by')
            .eq('board_key', selectedBoardKey)
            .is('deleted_at', null)
            .order('lane_id', { ascending: true })
            .order('status', { ascending: true })
            .order('position', { ascending: true })
            .order('created_at', { ascending: true }),
        ]);

        if (lanesResult.error) throw lanesResult.error;
        if (cardsResult.error) throw cardsResult.error;

        setLanes(sortLanes((lanesResult.data || []) as KanbanLaneRow[]));
        setCards(sortCards((cardsResult.data || []) as KanbanCardRow[]));
      } catch (error) {
        console.error('Failed to load kanban board:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBoard();
  }, [selectedBoardKey]);

  useEffect(() => {
    if (!trashOpen) return;
    loadTrashItems();
  }, [trashOpen, loadTrashItems]);

  useEffect(() => {
    setAddingNoteTarget(null);
    setNewNoteTitle('');
    setEditingCardId(null);
    setEditingLaneId(null);
    setLaneToDelete(null);
    setCardToDelete(null);
    setCardToMove(null);
    setCollapsedLaneIds(new Set());
    setDropTarget(null);
    setDraggingCardId(null);
  }, [selectedBoardKey]);

  useEffect(() => {
    const boardFilter = `board_key=eq.${selectedBoardKey}`;
    const channel = directSupabase
      .channel(`jarvis-kanban-live-${selectedBoardKey}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: LANES_TABLE, filter: boardFilter }, (payload: any) => {
        const inserted = payload.new as KanbanLaneRow;
        if (inserted.deleted_at) return;
        setLanes((prev) => sortLanes([...prev.filter((lane) => lane.id !== inserted.id), inserted]));
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: LANES_TABLE, filter: boardFilter }, (payload: any) => {
        const updated = payload.new as KanbanLaneRow;
        if (updated.deleted_at) {
          setLanes((prev) => prev.filter((lane) => lane.id !== updated.id));
          setCards((prev) => prev.filter((card) => card.lane_id !== updated.id));
          return;
        }
        setLanes((prev) => {
          const exists = prev.some((lane) => lane.id === updated.id);
          if (!exists) return sortLanes([...prev, updated]);
          return sortLanes(prev.map((lane) => (lane.id === updated.id ? updated : lane)));
        });
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: LANES_TABLE, filter: boardFilter }, (payload: any) => {
        const deletedId = (payload.old as { id: string }).id;
        setLanes((prev) => prev.filter((lane) => lane.id !== deletedId));
        setCards((prev) => prev.filter((card) => card.lane_id !== deletedId));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: CARDS_TABLE, filter: boardFilter }, (payload: any) => {
        const inserted = payload.new as KanbanCardRow;
        if (inserted.deleted_at) return;
        setCards((prev) => sortCards([...prev.filter((card) => card.id !== inserted.id), inserted]));
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: CARDS_TABLE, filter: boardFilter }, (payload: any) => {
        const updated = payload.new as KanbanCardRow;
        if (updated.deleted_at) {
          setCards((prev) => prev.filter((card) => card.id !== updated.id));
          return;
        }
        setCards((prev) => {
          const exists = prev.some((card) => card.id === updated.id);
          if (!exists) return sortCards([...prev, updated]);
          return sortCards(prev.map((card) => (card.id === updated.id ? updated : card)));
        });
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: CARDS_TABLE, filter: boardFilter }, (payload: any) => {
        const deletedId = (payload.old as { id: string }).id;
        setCards((prev) => prev.filter((card) => card.id !== deletedId));
      })
      .subscribe();

    return () => {
      directSupabase.removeChannel(channel);
    };
  }, [selectedBoardKey]);

  const groupedCards = useMemo(() => {
    const byLane: Record<string, Record<KanbanStatus, KanbanCardRow[]>> = {};

    lanes.forEach((lane) => {
      byLane[lane.id] = {
        todo: [],
        in_progress: [],
        done: [],
        rejected_nice_to_have: [],
      };
    });

    cards.forEach((card) => {
      if (!byLane[card.lane_id]) return;
      byLane[card.lane_id][card.status].push(card);
    });

    Object.values(byLane).forEach((laneColumns) => {
      COLUMNS.forEach((column) => {
        laneColumns[column.id].sort((a, b) => a.position - b.position);
      });
    });

    return byLane;
  }, [cards, lanes]);

  const handleAddCard = async () => {
    const title = newNoteTitle.trim();
    if (!title || !user?.id || !addingNoteTarget) return;

    try {
      setSubmittingNote(true);
      const cardsInLane = cards.filter((card) => card.lane_id === addingNoteTarget.laneId);
      const nextPosition = getNextPosition(cardsInLane, addingNoteTarget.status);

      const { data, error } = await (directSupabase as any)
        .from(CARDS_TABLE)
        .insert({
          title,
          status: addingNoteTarget.status,
          board_key: selectedBoardKey,
          lane_id: addingNoteTarget.laneId,
          position: nextPosition,
          created_by: user.id,
        })
        .select('id,title,status,board_key,lane_id,position,created_at,updated_at,deleted_at,deleted_by')
        .single();

      if (error) throw error;
      if (data) {
        setCards((prev) => sortCards([...prev.filter((card) => card.id !== data.id), data as KanbanCardRow]));
      }
      setAddingNoteTarget(null);
      setNewNoteTitle('');
    } catch (error) {
      console.error('Failed to add card:', error);
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleAddLane = async () => {
    if (!user?.id) return;

    try {
      setAddingLane(true);
      const nextPosition = (lanes.length ? Math.max(...lanes.map((lane) => lane.position || 0)) : 0) + 1;

      const { data, error } = await (directSupabase as any)
        .from(LANES_TABLE)
        .insert({
          title: DEFAULT_LANE_PLACEHOLDER,
          position: nextPosition,
          is_default: false,
          board_key: selectedBoardKey,
          created_by: user.id,
        })
        .select('id,title,position,is_default,board_key,created_at,updated_at,deleted_at,deleted_by')
        .single();

      if (error) throw error;
      if (data) {
        const lane = data as KanbanLaneRow;
        setLanes((prev) => sortLanes([...prev, lane]));
        setEditingLaneId(lane.id);
        setEditingLaneTitle(lane.title || DEFAULT_LANE_PLACEHOLDER);
      }
    } catch (error) {
      console.error('Failed to add lane:', error);
    } finally {
      setAddingLane(false);
    }
  };

  const moveCardToTarget = async (cardId: string, laneId: string, nextStatus: KanbanStatus) => {
    const current = cards.find((card) => card.id === cardId);
    if (!current) return;
    if (current.lane_id === laneId && current.status === nextStatus) return;

    const cardsInLane = cards.filter((card) => card.lane_id === laneId);
    const nextPosition = getNextPosition(cardsInLane, nextStatus);
    const optimistic: KanbanCardRow = {
      ...current,
      board_key: selectedBoardKey,
      lane_id: laneId,
      status: nextStatus,
      position: nextPosition,
    };

    setCards((prev) => sortCards(prev.map((card) => (card.id === cardId ? optimistic : card))));

    try {
      const { error } = await (directSupabase as any)
        .from(CARDS_TABLE)
        .update({
          board_key: selectedBoardKey,
          lane_id: laneId,
          status: nextStatus,
          position: nextPosition,
        })
        .eq('id', cardId);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to move card:', error);
      setCards((prev) => sortCards(prev.map((card) => (card.id === current.id ? current : card))));
    }
  };

  const startCardEdit = (card: KanbanCardRow) => {
    setEditingCardId(card.id);
    setEditingCardTitle(card.title);
  };

  const saveCardEdit = async (card: KanbanCardRow) => {
    const title = editingCardTitle.trim();
    setEditingCardId(null);
    if (!title || title === card.title) return;

    const optimistic = { ...card, title };
    setCards((prev) => prev.map((row) => (row.id === card.id ? optimistic : row)));

    try {
      const { error } = await (directSupabase as any)
        .from(CARDS_TABLE)
        .update({ title })
        .eq('id', card.id);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to update card title:', error);
      setCards((prev) => prev.map((row) => (row.id === card.id ? card : row)));
    }
  };

  const softDeleteCard = async (card: KanbanCardRow) => {
    if (!user?.id) return;
    setDeletingCardId(card.id);
    const previousCards = cards;
    setCards((prev) => prev.filter((row) => row.id !== card.id));

    try {
      const { error } = await (directSupabase as any)
        .from(CARDS_TABLE)
        .update({
          deleted_at: new Date().toISOString(),
          deleted_by: user.id,
        })
        .eq('id', card.id)
        .is('deleted_at', null);

      if (error) throw error;
      if (trashOpen) loadTrashItems();
    } catch (error) {
      console.error('Failed to delete card:', error);
      setCards(previousCards);
    } finally {
      setDeletingCardId(null);
      setCardToDelete(null);
      if (editingCardId === card.id) {
        setEditingCardId(null);
      }
    }
  };

  const startLaneEdit = (lane: KanbanLaneRow) => {
    if (lane.id === defaultLaneId) return;
    setEditingLaneId(lane.id);
    setEditingLaneTitle(lane.title);
  };

  const saveLaneEdit = async (lane: KanbanLaneRow) => {
    const title = editingLaneTitle.trim();
    setEditingLaneId(null);
    if (!title || title === lane.title) return;

    const optimistic = { ...lane, title };
    setLanes((prev) => sortLanes(prev.map((row) => (row.id === lane.id ? optimistic : row))));

    try {
      const { error } = await (directSupabase as any)
        .from(LANES_TABLE)
        .update({ title })
        .eq('id', lane.id);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to update lane title:', error);
      setLanes((prev) => sortLanes(prev.map((row) => (row.id === lane.id ? lane : row))));
    }
  };

  const confirmDeleteLane = async () => {
    if (!laneToDelete || !user?.id) return;
    if (laneToDelete.is_default) {
      setLaneToDelete(null);
      return;
    }

    const lane = laneToDelete;
    setDeletingLaneId(lane.id);

    const previousLanes = lanes;
    const previousCards = cards;
    setLanes((prev) => prev.filter((row) => row.id !== lane.id));
    setCards((prev) => prev.filter((row) => row.lane_id !== lane.id));

    try {
      const deletedAt = new Date().toISOString();

      const [laneUpdate, cardsUpdate] = await Promise.all([
        (directSupabase as any)
          .from(LANES_TABLE)
          .update({ deleted_at: deletedAt, deleted_by: user.id })
          .eq('id', lane.id)
          .is('deleted_at', null),
        (directSupabase as any)
          .from(CARDS_TABLE)
          .update({ deleted_at: deletedAt, deleted_by: user.id })
          .eq('lane_id', lane.id)
          .is('deleted_at', null),
      ]);

      if (laneUpdate.error) throw laneUpdate.error;
      if (cardsUpdate.error) throw cardsUpdate.error;

      setLaneToDelete(null);
      if (editingLaneId === lane.id) setEditingLaneId(null);
      if (addingNoteTarget?.laneId === lane.id) {
        setAddingNoteTarget(null);
        setNewNoteTitle('');
      }
      if (trashOpen) loadTrashItems();
    } catch (error) {
      console.error('Failed to delete lane:', error);
      setLanes(previousLanes);
      setCards(previousCards);
    } finally {
      setDeletingLaneId(null);
    }
  };

  const restoreCard = async (card: KanbanCardRow) => {
    setRestoringCardId(card.id);
    try {
      const { error } = await (directSupabase as any)
        .from(CARDS_TABLE)
        .update({ deleted_at: null, deleted_by: null })
        .eq('id', card.id);

      if (error) throw error;
      setDeletedCards((prev) => prev.filter((row) => row.id !== card.id));
    } catch (error) {
      console.error('Failed to restore card:', error);
    } finally {
      setRestoringCardId(null);
    }
  };

  const restoreLane = async (lane: KanbanLaneRow) => {
    setRestoringLaneId(lane.id);
    try {
      const [laneUpdate, cardsUpdate] = await Promise.all([
        (directSupabase as any)
          .from(LANES_TABLE)
          .update({ deleted_at: null, deleted_by: null })
          .eq('id', lane.id),
        (directSupabase as any)
          .from(CARDS_TABLE)
          .update({ deleted_at: null, deleted_by: null })
          .eq('lane_id', lane.id),
      ]);

      if (laneUpdate.error) throw laneUpdate.error;
      if (cardsUpdate.error) throw cardsUpdate.error;

      setDeletedLanes((prev) => prev.filter((row) => row.id !== lane.id));
      setDeletedCards((prev) => prev.filter((row) => row.lane_id !== lane.id));
    } catch (error) {
      console.error('Failed to restore lane:', error);
    } finally {
      setRestoringLaneId(null);
    }
  };

  const handleDropOnColumn = async (laneId: string, status: KanbanStatus) => {
    if (!draggingCardId) return;
    await moveCardToTarget(draggingCardId, laneId, status);
    setDraggingCardId(null);
    setDropTarget(null);
  };

  const toggleLaneCollapsed = (laneId: string) => {
    setCollapsedLaneIds((prev) => {
      const next = new Set(prev);
      if (next.has(laneId)) next.delete(laneId);
      else next.add(laneId);
      return next;
    });
  };

  const moveLane = async (laneId: string, direction: 'up' | 'down') => {
    const currentIndex = laneIdsInOrder.indexOf(laneId);
    if (currentIndex < 0) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= lanes.length) return;

    const lane = lanes[currentIndex];
    const targetLane = lanes[targetIndex];
    if (!lane || !targetLane) return;

    // Keep default lane pinned at top.
    if (lane.is_default || targetLane.is_default) return;

    const previousLanes = lanes;
    const updatedLane = { ...lane, position: targetLane.position };
    const updatedTargetLane = { ...targetLane, position: lane.position };

    setMovingLaneId(laneId);
    setLanes((prev) =>
      sortLanes(
        prev.map((row) => {
          if (row.id === updatedLane.id) return updatedLane;
          if (row.id === updatedTargetLane.id) return updatedTargetLane;
          return row;
        })
      )
    );

    try {
      const [a, b] = await Promise.all([
        (directSupabase as any)
          .from(LANES_TABLE)
          .update({ position: updatedLane.position })
          .eq('id', updatedLane.id),
        (directSupabase as any)
          .from(LANES_TABLE)
          .update({ position: updatedTargetLane.position })
          .eq('id', updatedTargetLane.id),
      ]);

      if (a.error) throw a.error;
      if (b.error) throw b.error;
    } catch (error) {
      console.error('Failed to reorder lanes:', error);
      setLanes(previousLanes);
    } finally {
      setMovingLaneId(null);
    }
  };

  const openMoveModal = (card: KanbanCardRow) => {
    setCardToMove(card);
    setMoveTargetLaneId(card.lane_id);
    setMoveTargetStatus(card.status);
  };

  const confirmMoveCard = async () => {
    if (!cardToMove || !moveTargetLaneId || !moveTargetStatus) return;
    setMovingCardId(cardToMove.id);
    try {
      await moveCardToTarget(cardToMove.id, moveTargetLaneId, moveTargetStatus);
      setCardToMove(null);
    } finally {
      setMovingCardId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size="lg" />
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, pb: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography level="h2">JARVIS Kanban</Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          <Select
            value={selectedBoardKey}
            onChange={(_, value) => setSelectedBoardKey((value as KanbanBoardKey) || 'general')}
            sx={{ minWidth: 170, flexGrow: { xs: 1, sm: 0 } }}
          >
            {BOARD_OPTIONS.map((board) => (
              <Option key={board.key} value={board.key}>
                {board.label}
              </Option>
            ))}
          </Select>
          <Button variant="outlined" color="neutral" onClick={() => setTrashOpen(true)}>
            Trash
          </Button>
          <IconButton
            variant="outlined"
            color="primary"
            onClick={handleAddLane}
            loading={addingLane}
            disabled={!user?.id}
            title="Add swimlane"
          >
            <IconWrapper icon={FaPlus} />
          </IconButton>
        </Stack>
      </Stack>

      {lanes.length === 0 ? (
        <Card variant="outlined" sx={{ p: 2 }}>
          <Typography level="body-sm" color="neutral">
            Create your first swimlane to start using the board.
          </Typography>
        </Card>
      ) : (
        <Stack spacing={2}>
          {lanes.map((lane) => (
            <Card key={lane.id} variant="outlined" sx={{ p: 2 }}>
              {(() => {
                const laneIndex = laneIdsInOrder.indexOf(lane.id);
                const canMoveUp = !lane.is_default && laneIndex > 0 && !lanes[laneIndex - 1]?.is_default;
                const canMoveDown = !lane.is_default && laneIndex >= 0 && laneIndex < laneIdsInOrder.length - 1;
                const isCollapsed = collapsedLaneIds.has(lane.id);

                return (
                  <>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                sx={{ mb: 1.5 }}
                spacing={1}
              >
                {editingLaneId === lane.id ? (
                  <Input
                    autoFocus
                    value={editingLaneTitle}
                    onChange={(event) => setEditingLaneTitle(event.target.value)}
                    onBlur={() => saveLaneEdit(lane)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        saveLaneEdit(lane);
                      } else if (event.key === 'Escape') {
                        setEditingLaneId(null);
                      }
                    }}
                    sx={{ minWidth: 260 }}
                  />
                ) : (
                  <Button variant="plain" onClick={() => startLaneEdit(lane)} sx={{ px: 0, justifyContent: 'flex-start' }}>
                    <Typography
                      level="title-md"
                      sx={{
                        color: lane.title === DEFAULT_LANE_PLACEHOLDER ? 'neutral.500' : undefined,
                        fontStyle: lane.title === DEFAULT_LANE_PLACEHOLDER ? 'italic' : 'normal',
                      }}
                    >
                      {lane.title}
                    </Typography>
                  </Button>
                )}
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => toggleLaneCollapsed(lane.id)}
                  >
                    <IconWrapper icon={isCollapsed ? FaChevronDown : FaChevronUp} />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    disabled={!canMoveUp || movingLaneId === lane.id}
                    onClick={() => moveLane(lane.id, 'up')}
                  >
                    <IconWrapper icon={FaArrowUp} />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    disabled={!canMoveDown || movingLaneId === lane.id}
                    onClick={() => moveLane(lane.id, 'down')}
                  >
                    <IconWrapper icon={FaArrowDown} />
                  </IconButton>
                  {!lane.is_default && lane.id !== defaultLaneId && (
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      loading={deletingLaneId === lane.id}
                      onClick={() => setLaneToDelete(lane)}
                    >
                      <IconWrapper icon={FaTrash} />
                    </IconButton>
                  )}
                </Stack>
              </Stack>

              {!isCollapsed && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                  gap: 2,
                }}
              >
                {COLUMNS.map((column) => {
                  const targetKey = `${lane.id}:${column.id}`;
                  const isDropTarget = dropTarget === targetKey;
                  const laneCards = groupedCards[lane.id]?.[column.id] || [];

                  return (
                    <Card
                      key={`${lane.id}-${column.id}`}
                      variant="outlined"
                      onDragOver={(event) => {
                        event.preventDefault();
                        setDropTarget(targetKey);
                      }}
                      onDragLeave={() => setDropTarget((prev) => (prev === targetKey ? null : prev))}
                      onDrop={(event) => {
                        event.preventDefault();
                        handleDropOnColumn(lane.id, column.id);
                      }}
                      sx={{
                        minHeight: 240,
                        borderStyle: isDropTarget ? 'solid' : 'dashed',
                        borderColor: isDropTarget ? 'primary.400' : 'neutral.300',
                        backgroundColor: isDropTarget ? 'primary.softBg' : 'background.surface',
                        transition: 'all 0.15s ease-in-out',
                        '& .add-note-trigger': {
                          opacity: 0,
                          pointerEvents: 'none',
                          transition: 'opacity 0.15s ease-in-out',
                        },
                        '&:hover .add-note-trigger': {
                          opacity: 1,
                          pointerEvents: 'auto',
                        },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography level="title-sm">{column.label}</Typography>
                        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                          {laneCards.length}
                        </Typography>
                      </Stack>

                      <Stack spacing={1}>
                        <Button
                          className="add-note-trigger"
                          size="sm"
                          variant="soft"
                          color="neutral"
                          onClick={() => {
                            setAddingNoteTarget({ laneId: lane.id, status: column.id });
                            setNewNoteTitle('');
                          }}
                          disabled={!user?.id}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          + Add note
                        </Button>

                        {addingNoteTarget?.laneId === lane.id && addingNoteTarget?.status === column.id && (
                          <Card
                            variant="outlined"
                            sx={{
                              p: 1,
                              backgroundColor: '#fff3a3',
                              border: '1px solid #d9cc7b',
                              boxShadow: 'sm',
                            }}
                          >
                            <Stack spacing={1}>
                              <Input
                                autoFocus
                                value={newNoteTitle}
                                placeholder="Write a quick post-it note..."
                                onChange={(event) => setNewNoteTitle(event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    handleAddCard();
                                  } else if (event.key === 'Escape') {
                                    setAddingNoteTarget(null);
                                    setNewNoteTitle('');
                                  }
                                }}
                                disabled={submittingNote}
                              />
                              <Stack direction="row" spacing={1}>
                                <Button size="sm" onClick={handleAddCard} loading={submittingNote} disabled={!newNoteTitle.trim() || !user?.id}>
                                  Add
                                </Button>
                                <Button
                                  size="sm"
                                  variant="plain"
                                  color="neutral"
                                  onClick={() => {
                                    setAddingNoteTarget(null);
                                    setNewNoteTitle('');
                                  }}
                                  disabled={submittingNote}
                                >
                                  Cancel
                                </Button>
                              </Stack>
                            </Stack>
                          </Card>
                        )}

                        {laneCards.map((card) => (
                          <Card
                            key={card.id}
                            draggable={editingCardId !== card.id}
                            onDragStart={() => setDraggingCardId(card.id)}
                            onDragEnd={() => {
                              setDraggingCardId(null);
                              setDropTarget(null);
                            }}
                            sx={{
                              p: 1.25,
                              backgroundColor: '#fff3a3',
                              border: '1px solid #d9cc7b',
                              boxShadow: 'sm',
                              cursor: editingCardId === card.id ? 'text' : 'grab',
                              '&:active': { cursor: editingCardId === card.id ? 'text' : 'grabbing' },
                            }}
                          >
                            {editingCardId === card.id ? (
                              <Stack spacing={1}>
                                <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                                  <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openMoveModal(card);
                                    }}
                                    sx={{ display: { xs: 'inline-flex', lg: 'none' } }}
                                  >
                                    <IconWrapper icon={FaExchangeAlt} />
                                  </IconButton>
                                  <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setEditingCardId(null);
                                    }}
                                  >
                                    <IconWrapper icon={FaPencilAlt} />
                                  </IconButton>
                                  <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="danger"
                                    loading={deletingCardId === card.id}
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setCardToDelete(card);
                                    }}
                                  >
                                    <IconWrapper icon={FaTrash} />
                                  </IconButton>
                                </Stack>
                                <Input
                                  autoFocus
                                  value={editingCardTitle}
                                  onChange={(event) => setEditingCardTitle(event.target.value)}
                                  onBlur={() => saveCardEdit(card)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                      event.preventDefault();
                                      saveCardEdit(card);
                                    } else if (event.key === 'Escape') {
                                      setEditingCardId(null);
                                    }
                                  }}
                                />
                              </Stack>
                            ) : (
                              <Stack spacing={0.5}>
                                <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                                  <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openMoveModal(card);
                                    }}
                                    sx={{ display: { xs: 'inline-flex', lg: 'none' } }}
                                  >
                                    <IconWrapper icon={FaExchangeAlt} />
                                  </IconButton>
                                  <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      startCardEdit(card);
                                    }}
                                  >
                                    <IconWrapper icon={FaPencilAlt} />
                                  </IconButton>
                                  <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="danger"
                                    loading={deletingCardId === card.id}
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setCardToDelete(card);
                                    }}
                                  >
                                    <IconWrapper icon={FaTrash} />
                                  </IconButton>
                                </Stack>
                                <Typography
                                  level="body-sm"
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    color: '#1f1f1f',
                                    fontWeight: 600,
                                    fontSize: '18px',
                                  }}
                                >
                                  {card.title}
                                </Typography>
                              </Stack>
                            )}
                          </Card>
                        ))}
                      </Stack>
                    </Card>
                  );
                })}
              </Box>
              )}
                  </>
                );
              })()}
            </Card>
          ))}
        </Stack>
      )}

      <Modal open={!!laneToDelete} onClose={() => (deletingLaneId ? null : setLaneToDelete(null))}>
        <ModalDialog variant="outlined" color="neutral">
          <DialogTitle>Delete Swimlane</DialogTitle>
          <DialogContent>
            <Typography level="body-sm">
              Are you sure you want to delete{' '}
              <Typography component="span" sx={{ fontWeight: 700 }}>
                {laneToDelete?.title || 'this swimlane'}
              </Typography>
              ? This will move the swimlane and all of its notes to Trash.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" color="neutral" onClick={() => setLaneToDelete(null)} disabled={!!deletingLaneId}>
              Cancel
            </Button>
            <Button color="danger" onClick={confirmDeleteLane} loading={!!deletingLaneId}>
              Move to Trash
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={!!cardToDelete} onClose={() => (deletingCardId ? null : setCardToDelete(null))}>
        <ModalDialog variant="outlined" color="neutral">
          <DialogTitle>Delete Note</DialogTitle>
          <DialogContent>
            <Typography level="body-sm">
              Are you sure you want to delete this note? It will be moved to Trash and can be restored.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setCardToDelete(null)}
              disabled={!!deletingCardId}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={() => {
                if (cardToDelete) softDeleteCard(cardToDelete);
              }}
              loading={!!deletingCardId}
            >
              Move to Trash
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={!!cardToMove} onClose={() => (movingCardId ? null : setCardToMove(null))}>
        <ModalDialog variant="outlined" color="neutral">
          <DialogTitle>Move Note</DialogTitle>
          <DialogContent>
            <Stack spacing={1.25}>
              <Typography level="body-sm">
                Choose a swimlane and section for this note.
              </Typography>
              <Select
                value={moveTargetLaneId || null}
                onChange={(_, value) => setMoveTargetLaneId(String(value || ''))}
                placeholder="Choose swimlane"
              >
                {lanes.map((lane) => (
                  <Option key={lane.id} value={lane.id}>
                    {lane.title}
                  </Option>
                ))}
              </Select>
              <Select
                value={moveTargetStatus}
                onChange={(_, value) => setMoveTargetStatus((value as KanbanStatus) || 'todo')}
                placeholder="Choose section"
              >
                {COLUMNS.map((column) => (
                  <Option key={column.id} value={column.id}>
                    {column.label}
                  </Option>
                ))}
              </Select>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setCardToMove(null)}
              disabled={!!movingCardId}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={confirmMoveCard}
              loading={!!movingCardId}
              disabled={!moveTargetLaneId}
            >
              Move
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={trashOpen} onClose={() => setTrashOpen(false)}>
        <ModalDialog variant="outlined" color="neutral" sx={{ width: 'min(880px, 95vw)', maxHeight: '85vh', overflow: 'auto' }}>
          <DialogTitle>Kanban Trash</DialogTitle>
          <DialogContent>
            <Typography level="body-sm" sx={{ mb: 1.5 }}>
              Deleted items are kept here and can be restored.
            </Typography>

            {trashLoading ? (
              <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size="sm" />
              </Box>
            ) : (
              <Stack spacing={2}>
                <Card variant="outlined" sx={{ p: 1.5 }}>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Deleted Swimlanes
                  </Typography>
                  {deletedLanes.length === 0 ? (
                    <Typography level="body-sm" color="neutral">
                      No deleted swimlanes.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {deletedLanes.map((lane) => (
                        <Stack key={lane.id} direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography level="body-sm">{lane.title}</Typography>
                            <Typography level="body-xs" color="neutral">
                              Deleted {lane.deleted_at ? new Date(lane.deleted_at).toLocaleString() : 'recently'}
                            </Typography>
                          </Box>
                          <Button
                            size="sm"
                            variant="soft"
                            color="success"
                            loading={restoringLaneId === lane.id}
                            onClick={() => restoreLane(lane)}
                          >
                            Restore
                          </Button>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Card>

                <Card variant="outlined" sx={{ p: 1.5 }}>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Deleted Notes
                  </Typography>
                  {deletedCards.length === 0 ? (
                    <Typography level="body-sm" color="neutral">
                      No deleted notes.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {deletedCards.map((card) => (
                        <Stack key={card.id} direction="row" justifyContent="space-between" alignItems="center">
                          <Box sx={{ mr: 1 }}>
                            <Typography level="body-sm">{card.title}</Typography>
                            <Typography level="body-xs" color="neutral">
                              Deleted {card.deleted_at ? new Date(card.deleted_at).toLocaleString() : 'recently'}
                            </Typography>
                          </Box>
                          <Button
                            size="sm"
                            variant="soft"
                            color="success"
                            loading={restoringCardId === card.id}
                            onClick={() => restoreCard(card)}
                          >
                            Restore
                          </Button>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Card>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="plain" color="neutral" onClick={() => setTrashOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default JarvisKanbanPage;
